import React from 'react';
import { LandmarkIcon } from 'lucide-react';
import { COST_RANGE, DUTY_DISCLAIMER, FIGURES_AS_AT, STATE_DUTY, STATE_ORDER } from '../../data/stampDuty';
import { formatCurrency } from '../../utils/roadmapCalcs';
interface StampDutyPanelProps {
  /** Their target price, used to turn the percentage range into real money. */
  propertyPrice: number;
  /** Investors never see the concession caps — none of them can apply. */
  mode: 'first-home' | 'investment';
}

/**
 * Reference panel above the purchasing costs field.
 *
 * Deliberately read-only: no state toggle, no price toggle, no calculated
 * figure to accept. Someone at this point in the flow is being asked for ONE
 * number, and a second interactive widget competing for their attention pushes
 * them into fiddling with estimates instead of answering. This informs, then
 * gets out of the way — they type their own figure or take the typical
 * estimate below.
 */
export function StampDutyPanel({
  propertyPrice,
  mode
}: StampDutyPanelProps) {
  const isFirstHome = mode === 'first-home';
  const band = isFirstHome ? COST_RANGE.firstHome : COST_RANGE.investment;
  const hasPrice = propertyPrice > 0;
  const low = Math.round(propertyPrice * band.low / 500) * 500;
  const high = Math.round(propertyPrice * band.high / 500) * 500;
  return <section className="mt-6 rounded-2xl border border-primary-500/30 bg-primary-50 dark:bg-primary-500/10 p-5" aria-label={isFirstHome ? 'First home buyer stamp duty concessions' : 'Stamp duty on an investment purchase'} data-id="element-438" data-mp="3dbqmwzh6pywj">
      <h3 className="flex items-center gap-2 text-sm font-semibold text-primary-800 dark:text-primary-300" data-id="element-439" data-mp="2pmxwfijc0j9b">
        <LandmarkIcon className="w-4 h-4" data-id="element-440" data-mp="2umu17jzusepx" />
        {isFirstHome ? 'What people usually pay' : 'Stamp duty on an investment'}
      </h3>

      {/* The range, not a single number — duty is the biggest component and it
          swings on whether a concession applies. */}
      <p className="mt-4 text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-white tabular-nums" data-id="element-441" data-mp="3gdbnuho5hwi1">
        {hasPrice ? `${formatCurrency(low)} – ${formatCurrency(high)}` : `${Math.round(band.low * 100)}% – ${Math.round(band.high * 100)}%`}
      </p>
      <p className="mt-1 text-sm text-slate-600 dark:text-slate-300 leading-relaxed" data-id="element-442" data-mp="3q9f0ihgmpdj8">
        {hasPrice ? <>
            Roughly {Math.round(band.low * 100)}% to{' '}
            {Math.round(band.high * 100)}% of a{' '}
            {formatCurrency(propertyPrice)} purchase.{' '}
          </> : <>Of the purchase price. </>}
        {isFirstHome ? 'The low end assumes a stamp duty concession applies. The high end assumes you pay the full rate.' : 'Investment purchases pay the full rate of duty — there is no concession to apply.'}
      </p>

      {isFirstHome && <div className="mt-5 pt-4 border-t border-primary-500/20" data-id="element-443" data-mp="29y1avdw26hhw">
          <p className="text-sm font-semibold text-slate-900 dark:text-white" data-id="element-444" data-mp="29vosx0qaw74u">
            Where duty stops applying
          </p>
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400 leading-relaxed" data-id="element-445" data-mp="u6pbwvyoa9oq">
            Buy under your state's cap as an eligible first home buyer and you
            generally pay no duty at all.
          </p>
          <dl className="mt-3 space-y-1.5" data-id="element-446" data-mp="kh5pxhfnxzpn">
            {STATE_ORDER.map(code => {
          const {
            kind,
            nilUpTo,
            concessionUpTo
          } = STATE_DUTY[code];
          let value = 'No duty concession';
          if (kind === 'exempt') {
            value = 'No price cap';
          } else if (kind === 'threshold' && nilUpTo) {
            value = `Up to ${formatCurrency(nilUpTo)}`;
          }
          const partial = kind === 'threshold' && concessionUpTo && nilUpTo && concessionUpTo > nilUpTo ? `then partial to ${formatCurrency(concessionUpTo)}` : null;
          return <div key={code} className="flex items-baseline justify-between gap-4" data-id="element-447" data-mp="80603tci2ov4">
                  <dt className="text-sm font-medium text-slate-600 dark:text-slate-300 flex-shrink-0" data-id="element-448" data-mp="3ue66p25jgw1a">
                    {code}
                  </dt>
                  <dd className="text-sm text-slate-900 dark:text-white tabular-nums text-right" data-id="element-449" data-mp="3pgxlesp3gbvt">
                    {value}
                    {partial && <span className="block text-xs text-slate-400 dark:text-slate-500" data-id="element-450" data-mp="lycq6nb7jkk8">
                        {partial}
                      </span>}
                  </dd>
                </div>;
        })}
          </dl>
          <p className="mt-3 text-xs text-slate-500 dark:text-slate-400 leading-relaxed" data-id="element-451" data-mp="d0ai5nt23rng">
            SA applies to new homes only, and the ACT is income-tested rather
            than price-tested. Concessions generally require never having owned
            property anywhere.
          </p>
        </div>}

      <p className="mt-4 text-xs text-slate-400 dark:text-slate-500 leading-relaxed" data-id="element-452" data-mp="1h7pv00mr0cp7">
        Figures current as at {FIGURES_AS_AT}. {DUTY_DISCLAIMER}
      </p>
    </section>;
}